'use client';

import {
  NonSegmentEvents,
  buildEventProperties,
  trackD2CNonSegmentAnalytics,
} from '@/analytics';
import * as AUI from '@/lib/mx-asurion-ui-react';
import clsx from 'clsx';
import NextLink from 'next/link';
import { ComponentPropsWithoutRef, forwardRef } from 'react';

export type LinkProps = AuiProps &
  NextProps &
  AnalyticsProps & { buttonColor?: string | null };

type AuiProps = Omit<AUI.LinkProps, 'href'>;

type NextProps = Pick<
  ComponentPropsWithoutRef<typeof NextLink>,
  'href' | 'as' | 'replace' | 'scroll' | 'shallow' | 'prefetch' | 'locale'
>;

type AnalyticsProps = {
  analytics?: {
    contentful?: {
      id?: string;
      text?: string | null;
      name?: string | null;
    };
  };
};

const Link = forwardRef<HTMLAnchorElement, LinkProps>(function LinkComponent(
  {
    href,
    as,
    replace,
    scroll,
    shallow,
    prefetch,
    locale,
    children,
    onClick,
    analytics: { contentful } = {},
    buttonColor,
    ...rest
  },
  ref,
) {
  async function handleClick(event: React.MouseEvent<HTMLAnchorElement>) {
    onClick?.(event);

    await trackD2CNonSegmentAnalytics(
      NonSegmentEvents.LinkClicked,
      buildEventProperties({
        event: NonSegmentEvents.LinkClicked,
        extraData: {
          href,
          contentfulId: contentful?.id,
          contentfulText: contentful?.text,
          contentfulName: contentful?.name,
        },
      }),
    );
  }

  return (
    <NextLink
      href={href}
      as={as}
      replace={replace}
      scroll={scroll}
      shallow={shallow}
      prefetch={prefetch}
      locale={locale}
      passHref
      legacyBehavior
    >
      <AUI.Link
        {...{ ...rest, ref, onClick: handleClick }}
        className={clsx(rest.className, {
          '!border-white': buttonColor === 'white',
          '!bg-white': buttonColor === 'white',
          '!text-black': buttonColor === 'white',
        })}
      >
        {children}
      </AUI.Link>
    </NextLink>
  );
});

export default Link;
